// src/encounter/submitted-questionnaire-section.tsx
// MIDDLE-column "Special Form" section (PROD-1251) — renders the questionnaire the patient submitted
// at booking. PRESENTATIONAL: takes the mapped view model; the host fetches it. Mirrors Flutter
// pre_encounter_widget.dart appointment()/appointmentInfo(). The whole section is HIDDEN when there
// is no submitted questionnaire (Flutter body() gate) — i.e. `form` is null.
//
// WAVE 4 (L6): a THIN adapter over the DS `SubmittedQuestionnaireView` organism (B3 — the E1
// intake read-mode Q&A card, node 613:2014). The hand-built card/dl anatomy retired in the same
// motion. Host keeps: the null-gate, the load-error note (the DS view has no error state), and
// the view-model mapping. Honest-absent seams:
//   · per-answer `verified` checks — the wire (QuestionnaireSubmitted) carries no verified flag,
//     so no answer claims one; the legend keeps the shipped Flutter copy.
//   · `onPromoteAnswer` / `onAddAll` (I1 clinical actions) — SPEC-NEEDED, deliberately NOT passed
//     (PROD-1588 wave 5 verdict, 2026-07-22). Flutter has NO working precedent: the intake card
//     (pre_encounter_widget.dart) renders read-only; its "Add All" entry point is COMMENTED OUT
//     (:767-771) and the dialog it would open (add_to_patient_summary_widget.dart) has an EMPTY
//     no-op handler fed by the patient's existing summary list — not the questionnaire answers.
//     Nothing anywhere (Flutter or backend) maps an intake answer → a chart entity type: the wire
//     (SubmittedItem: text/type/answer) carries no linkId convention, question code, or extension
//     that says "this answer is an allergy vs a problem vs a med". Wiring these seams would mean
//     INVENTING that mapping (e.g. classifying by question text) — product behavior no one has
//     specified. When product specs the mapping (likely: authored intake templates carrying coded
//     items), promotion must reuse the rail's existing add flows (useSaveAllergy /
//     useSaveCondition / useSaveMedication + their dialogs) — never a parallel write path.
import { EmptyState, SubmittedQuestionnaireView } from '@scripsteam/scrips-signal-ds';
import { useDesignV2 } from './lib/design-v2';
import { EncBlock, EncSectionCard } from './enc-section-card';
import type { SubmittedFormView } from './lib/to-submitted-questionnaire';

export function SubmittedQuestionnaireSection({
  form,
  error,
  patientFirstName,
}: {
  form: SubmittedFormView | null | undefined;
  /** The submitted-QR fetch failed — show a note instead of silently hiding (Codex). */
  error?: boolean;
  /** Patient's first name for the not-yet-answered copy. Absent → a neutral phrasing. */
  patientFirstName?: string;
}) {
  // Design sheet v1.1 §1 States (switch ON only): the failed read uses the DS EmptyState.
  const designV2 = useDesignV2();
  // Frame v2 (design export 21-10, EncCoreSubmittedForm): one Subjective card, one block; the failed read, the unanswered form and
  // the DS view all sit in the block's body.
  if (designV2) {
    if (!form) {
      if (!error) return null;
      return (
        <EncSectionCard section="s" aria-label="Special form">
          <EncBlock title="Submitted form" icon="hpi">
            <EmptyState icon="warning" title="Couldn’t load the submitted questionnaire." />
          </EncBlock>
        </EncSectionCard>
      );
    }
    return (
      <EncSectionCard section="s" aria-label="Special form">
        <EncBlock title={form.name} icon="hpi">
          {form.rows.length === 0 ? (
            <div data-slot="pv-unanswered">
              {patientFirstName
                ? `${patientFirstName} has not answered the questionnaire yet.`
                : 'The patient has not answered the questionnaire yet.'}
            </div>
          ) : (
            <div data-slot="pv-form">
              <SubmittedQuestionnaireView
                title={form.name}
                updatedByName={form.updatedByName}
                rows={form.rows.map((r) => ({
                  id: r.id,
                  question: r.question,
                  answers: r.answers.map((text, i) => ({ id: `${r.id}-a${i}`, text })),
                }))}
                legend="Patient data verified"
              />
            </div>
          )}
        </EncBlock>
      </EncSectionCard>
    );
  }
  if (!form) {
    // Distinguish a failed load from a legitimate "no questionnaire" (which stays hidden, Flutter
    // parity). Loading is also hidden (non-blocking) — the section appears once data arrives.
    if (error) {
      return (
        <section aria-label="Special form">
          <p className="m-0 text-size-sm text-text-secondary italic">
            Couldn’t load the submitted questionnaire.
          </p>
        </section>
      );
    }
    return null; // no submitted questionnaire → section hidden (Flutter parity)
  }
  // A questionnaire IS assigned but carries no answers. Flutter renders a titled section with a
  // centred 100px box reading "{firstName} has not answered the questionnaire yet."
  // (appointmentInfo()'s empty branch), and PreEncounter.dc.html draws the same card. The DS view
  // has no empty state and never receives the patient, so the host owns this one.
  if (form.rows.length === 0) {
    return (
      <section
        aria-label="Special form"
        // gap-3 (10px): Tailwind's spacing step is an exact match, not an approximation.
        className="flex flex-col gap-3"
      >
        {/* text-size-2xs = --text-2xs (10px). */}
        <span className="font-bold text-size-2xs tracking-eyebrow uppercase text-text-hint">
          {form.name}
        </span>
        <div
          // min-h-45 (180px) and p-8 (32px) are steps of the DS 4px spacing scale.
          className="flex items-center justify-center min-h-45 p-8 bg-surface-card border border-border-default rounded-lg shadow-2"
        >
          <span className="text-size-sm text-text-hint">
            {patientFirstName
              ? `${patientFirstName} has not answered the questionnaire yet.`
              : 'The patient has not answered the questionnaire yet.'}
          </span>
        </div>
      </section>
    );
  }
  return (
    <SubmittedQuestionnaireView
      title={form.name}
      updatedByName={form.updatedByName}
      rows={form.rows.map((r) => ({
        id: r.id,
        question: r.question,
        // One answer per line (E1 anatomy — Choice/OpenChoice codings stack, never comma-join).
        answers: r.answers.map((text, i) => ({ id: `${r.id}-a${i}`, text })),
      }))}
      legend="Patient data verified"
    />
  );
}
